// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button, Modal, Spinner, TextArea, TextInput, useToast } from '@loa/ui-components';
import { proposalApi } from '../../../../../api/proposalApi';
import { quoteApi } from '../../../../../api/quoteApi';
import type {
  BreakthroughPensionOptionsSectionContent,
  BreakthroughProposalDetails,
  Proposal,
  ProposalSection,
} from '../../../../../types/proposal';
import type { Quote } from '../../../../../types/quote';
import { formatCurrency } from '../../../proposalDisplay';

export type SectionPensionOptionsProps = Readonly<{
  proposalId: string;
  /** The relationship — the linked-quote picker lists the profile's pension quotes. */
  profileId?: string;
  proposal: Proposal;
  section: ProposalSection;
  onSaved: (updated: ProposalSection) => void;
  onUpdateDetails: (partial: Partial<BreakthroughProposalDetails>) => Promise<void>;
  /**
   * Renders the "Client view" preview (BR-P5, PRD §5 / RQ-BR8): the linked
   * quote's figures + the Financial Position holdings table are already
   * read-only and render unchanged; the "link a quote" picker and the
   * recommended-fund/rationale editors + Save button are hidden. Defaults
   * to false (advisor editable builder).
   */
  readOnly?: boolean;
}>;

function apiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    return (err.response?.data as { message?: string } | undefined)?.message ?? fallback;
  }
  return fallback;
}

function readContent(section: ProposalSection): BreakthroughPensionOptionsSectionContent {
  return (section.content as BreakthroughPensionOptionsSectionContent | null) ?? {};
}

function readDetails(proposal: Proposal): BreakthroughProposalDetails {
  return (proposal.details as BreakthroughProposalDetails | null) ?? {};
}

/**
 * BR_PENSION_OPTIONS (PRD §4 row 9 / RQ-BR5) — an OPTIONAL linked quote
 * (persisted on the proposal's detail row) supplies live figures; when
 * absent the section falls back to the client's Financial Position pension
 * holdings. `recommendedFund`/`rationale` are advisor-authored.
 */
export function SectionPensionOptions({
  proposalId,
  profileId,
  proposal,
  section,
  onSaved,
  onUpdateDetails,
  readOnly = false,
}: SectionPensionOptionsProps) {
  const { showToast } = useToast();
  const content = readContent(section);
  const details = readDetails(proposal);
  const pensions = content.pensions ?? [];

  const [recommendedFund, setRecommendedFund] = useState(content.recommendedFund ?? '');
  const [rationale, setRationale] = useState(content.rationale ?? '');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const [quotes, setQuotes] = useState<Quote[] | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSelection, setPickerSelection] = useState<string | null>(null);
  const [attaching, setAttaching] = useState(false);

  useEffect(() => {
    if (!profileId) return undefined;
    let cancelled = false;
    quoteApi
      .listQuotes(profileId, { quoteType: 'PENSION', size: 50 })
      .then((page) => {
        if (!cancelled) setQuotes(page.content);
      })
      .catch(() => {
        if (!cancelled) setQuotes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  const openPicker = useCallback(() => {
    setPickerSelection(details.linkedQuoteId ?? null);
    setPickerOpen(true);
  }, [details.linkedQuoteId]);

  const handleAttach = useCallback(async () => {
    if (attaching) return;
    setAttaching(true);
    try {
      await onUpdateDetails({ linkedQuoteId: pickerSelection ?? undefined });
      const refreshed = await proposalApi.getSections(proposalId);
      const updated = refreshed.find((s) => s.sectionKey === section.sectionKey);
      if (updated) onSaved(updated);
      setPickerOpen(false);
      showToast('Linked quote updated', 'success');
    } catch (err) {
      showToast(apiErrorMessage(err, 'Failed to link the quote'), 'error');
    } finally {
      setAttaching(false);
    }
  }, [attaching, onUpdateDetails, pickerSelection, proposalId, section.sectionKey, onSaved, showToast]);

  const handleSave = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      const updated = await proposalApi.updateSection(proposalId, section.sectionKey, {
        content: { ...content, recommendedFund: recommendedFund.trim() || undefined, rationale },
      });
      setDirty(false);
      onSaved(updated);
      showToast('Pension Options saved', 'success');
    } catch (err) {
      showToast(apiErrorMessage(err, 'Failed to save Pension Options'), 'error');
    } finally {
      setSaving(false);
    }
  }, [saving, proposalId, section.sectionKey, content, recommendedFund, rationale, onSaved, showToast]);

  return (
    <div className="pension-builder__section" data-testid="section-br-pension-options">
      {!readOnly && (
        <div className="pension-builder__cashflow-header">
          <h3 className="pension-builder__facts-card-title">Linked quote (optional)</h3>
          <Button
            variant="outlined"
            size="sm"
            onClick={openPicker}
            data-testid="section-br-pension-options-link-btn"
          >
            {details.linkedQuoteId ? 'Change linked quote' : 'Link a quote'}
          </Button>
        </div>
      )}

      {content.selected ? (
        <div className="pension-builder__facts-card" data-testid="section-br-pension-options-selected">
          <h4 className="pension-builder__facts-card-title">
            {content.selected.providerName ?? 'Recommended option'}
          </h4>
          <p>{formatCurrency(content.selected.figures?.amount, content.selected.figures?.currencyCode) ?? '—'}</p>
        </div>
      ) : (
        <p className="pension-builder__empty" data-testid="section-br-pension-options-no-quote">
          No quote linked — falling back to the client's Financial Position pension data below.
        </p>
      )}

      {pensions.length > 0 && (
        <table className="pension-builder__table" data-testid="section-br-pension-options-holdings">
          <thead>
            <tr>
              <th>Owner</th>
              <th>Policy</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {pensions.map((p, i) => (
              <tr key={p.policyId ?? i}>
                <td>{p.owner ?? '—'}</td>
                <td>{p.label ?? '—'}</td>
                <td>{formatCurrency(p.value, undefined) ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {readOnly ? (
        <>
          {recommendedFund && (
            <TextInput
              label="Recommended fund"
              value={recommendedFund}
              disabled
              readOnly
              fluid
              data-testid="section-br-pension-options-fund"
            />
          )}
          {rationale && (
            <TextArea
              label="Rationale"
              value={rationale}
              disabled
              readOnly
              rows={5}
              fluid
              data-testid="section-br-pension-options-rationale"
            />
          )}
        </>
      ) : (
        <>
          <TextInput
            label="Recommended fund"
            value={recommendedFund}
            onChange={(e) => {
              setRecommendedFund(e.target.value);
              setDirty(true);
            }}
            placeholder="e.g. Multi-Asset Fund 4"
            fluid
            data-testid="section-br-pension-options-fund"
          />
          <TextArea
            label="Rationale"
            value={rationale}
            onChange={(e) => {
              setRationale(e.target.value);
              setDirty(true);
            }}
            placeholder="Why these pension options make sense for the client…"
            rows={5}
            fluid
            data-testid="section-br-pension-options-rationale"
          />

          <div className="pension-builder__section-footer">
            <Button
              variant="filled"
              onClick={handleSave}
              disabled={saving || !dirty}
              loading={saving}
              data-testid="section-br-pension-options-save-btn"
            >
              Save
            </Button>
          </div>
        </>
      )}

      {!readOnly && (
      <Modal open={pickerOpen} onClose={() => setPickerOpen(false)} size="medium">
        <Modal.Header title="Link a pension quote" onClose={() => setPickerOpen(false)} />
        <Modal.Content>
          {quotes === null && <Spinner />}
          {quotes !== null && quotes.length === 0 && (
            <p className="pension-builder__empty" data-testid="section-br-pension-options-no-quotes">
              No pension quotes found for this relationship.
            </p>
          )}
          {quotes !== null && (
            <div className="pension-builder__scenario-list" role="listbox" aria-label="Pension quotes">
              <button
                type="button"
                role="option"
                aria-selected={pickerSelection === null}
                className={
                  pickerSelection === null
                    ? 'pension-builder__scenario-item pension-builder__scenario-item--selected'
                    : 'pension-builder__scenario-item'
                }
                onClick={() => setPickerSelection(null)}
                data-testid="section-br-pension-options-clear"
              >
                <span className="pension-builder__scenario-name">No linked quote</span>
              </button>
              {quotes.map((q) => (
                <button
                  key={q.id}
                  type="button"
                  role="option"
                  aria-selected={pickerSelection === q.id}
                  className={
                    pickerSelection === q.id
                      ? 'pension-builder__scenario-item pension-builder__scenario-item--selected'
                      : 'pension-builder__scenario-item'
                  }
                  onClick={() => setPickerSelection(q.id)}
                  data-testid={`section-br-pension-options-quote-${q.id}`}
                >
                  <span className="pension-builder__scenario-name">{q.quoteNumber}</span>
                  <span className="pension-builder__scenario-desc">
                    {q.clientNames} · {formatCurrency(q.amount, q.currencyCode) ?? '—'}
                  </span>
                </button>
              ))}
            </div>
          )}
        </Modal.Content>
        <Modal.Actions
          secondaryAction={{ label: 'Cancel', onClick: () => setPickerOpen(false), disabled: attaching }}
          primaryAction={{
            label: 'Save',
            onClick: () => {
              void handleAttach();
            },
            disabled: attaching,
            loading: attaching,
          }}
        />
      </Modal>
      )}
    </div>
  );
}

export default SectionPensionOptions;
